/* eslint-disable */
import { Modal, Form, Tree, Button, Flex, Typography, message, Input, Spin } from "antd";
import { EditOutlined, SearchOutlined } from "@ant-design/icons";
import React, { forwardRef, useImperativeHandle, useState, useMemo, useEffect } from "react";
import http from "@/utils/http";
import api from "@/utils/api";

const RoleMenuModal = forwardRef((props, ref) => {
  const { onSuccess } = props;
  const [form] = Form.useForm();
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [menuTreeData, setMenuTreeData] = useState([]);
  const [checkedKeys, setCheckedKeys] = useState([]);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [expandedKeysState, setExpandedKeysState] = useState([]);

useImperativeHandle(ref, () => ({
  showModal: async (record) => {
    setCurrentRecord(record);
    setVisible(true);
    setLoading(true);
    setSearchValue("");
    setCheckedKeys([]); 
    
    try {
      const params = { roleId: record.roleId };
      const response = await http.get(api.getMenuTreeByRoleId, { params });
      
      if (response.code === "1") {
        const { treeData, checkedKeys } = convertToTreeData(response.data);
        setMenuTreeData(treeData);
        setCheckedKeys(checkedKeys);
        
        const allKeys = getAllKeys(treeData);
        setExpandedKeysState(allKeys);
        
        // 只存储 record，不存储 menu
        form.setFieldsValue({ _record: record });
      } else {
        message.error(response.msg || "Failed to fetch menu data");
        setVisible(false);
      }
    } catch (error) {
      message.error("Failed to load menu data");
      setVisible(false);
    } finally {
      setLoading(false);
    }
  },
  closeModal: () => {
    setVisible(false);
    form.resetFields();
    setSearchValue("");
    setCheckedKeys([]);
  }
}));

  const convertToTreeData = (data) => {
    if (!data) return { treeData: [], checkedKeys: [] };
    const checkedKeys = [];

    const convert = (nodes) => {
      return nodes.map((item) => {
        if (item.sfyxz === 1) {
          checkedKeys.push(item.menuId.toString());
        }

        return {
          title: item.menuName,
          key: item.menuId.toString(),
          children: item.childNode?.length > 0 ? convert(item.childNode) : undefined,
        };
      });
    };

    const treeData = convert(data);
    return { treeData, checkedKeys };
  };

  // 高亮匹配的文本
  const highlightText = (text, search) => {
    if (!search) return text;
    const index = text.toLowerCase().indexOf(search.toLowerCase());
    if (index === -1) return text;
    
    return (
      <span>
        {text.substring(0, index)}
        <span style={{ color: '#1890ff', fontWeight: 'bold' }}>
          {text.substring(index, index + search.length)}
        </span>
        {text.substring(index + search.length)}
      </span>
    );
  };

  // 过滤树数据 - 保留所有节点，标记匹配项
  const filterTreeData = (data, searchText) => {
    if (!searchText) return data;
    
    const filter = (nodes) => {
      return nodes.reduce((acc, node) => {
        const children = node.children ? filter(node.children) : [];
        const titleMatch = node.title.toLowerCase().includes(searchText.toLowerCase());
        
        if (titleMatch || children.length > 0) {
          acc.push({
            ...node,
            children: children.length > 0 ? children : node.children,
            title: titleMatch ? highlightText(node.title, searchText) : node.title,
          });
        }
        return acc;
      }, []);
    };
    
    return filter(data);
  };

  // 获取需要展开的 keys（只展开匹配路径上的节点）
  const getExpandedKeys = (data, searchText) => {
    if (!searchText) return [];
    const keys = [];
    
    const findPath = (nodes, path = []) => {
      for (const node of nodes) {
        const currentPath = [...path, node.key];
        if (node.title.toLowerCase().includes(searchText.toLowerCase())) {
          keys.push(...currentPath);
        }
        if (node.children) {
          findPath(node.children, currentPath);
        }
      }
    };
    
    findPath(data);
    return keys;
  };

  // 获取树中所有的 key
  const getAllKeys = (data) => {
    const keys = [];
    const traverse = (nodes) => {
      nodes.forEach(node => {
        keys.push(node.key);
        if (node.children) {
          traverse(node.children);
        }
      });
    };
    traverse(data);
    return keys;
  };

  // 过滤 checkedKeys，只保留当前树中存在的 key
  const getValidCheckedKeys = (checkedKeys, treeData) => {
    if (!treeData || treeData.length === 0) return [];
    const allKeys = getAllKeys(treeData);
    return Array.isArray(checkedKeys) 
      ? checkedKeys.filter(key => allKeys.includes(key))
      : checkedKeys;
  };

  const filteredTreeData = useMemo(() => {
    return filterTreeData(menuTreeData, searchValue);
  }, [menuTreeData, searchValue]);

  // 当搜索变化时更新展开的 keys
  useEffect(() => {
    if (!searchValue) {
      // 搜索为空时，展开所有节点
      const allKeys = getAllKeys(menuTreeData);
      setExpandedKeysState(allKeys);
    } else {
      // 搜索时展开匹配路径
      const keys = getExpandedKeys(menuTreeData, searchValue);
      setExpandedKeysState(keys);
    }
  }, [searchValue, menuTreeData]);

  const validCheckedKeys = useMemo(() => {
    return getValidCheckedKeys(checkedKeys, filteredTreeData);
  }, [checkedKeys, filteredTreeData]);

  const handleSearch = (e) => {
    setSearchValue(e.target.value);
  };

const handleCheck = (checkedKeysValue, { checkedNodes, node, event }) => {

  const newCheckedKeys = Array.isArray(checkedKeysValue) 
    ? checkedKeysValue 
    : (checkedKeysValue?.checked || []);
  
  setCheckedKeys(newCheckedKeys);
  // 不需要再更新 form，直接使用 state
};


  // 处理用户手动展开/折叠
  const handleExpand = (keys) => {
    setExpandedKeysState(keys);
  };

const onFinishMenu = async () => {
  try {
    setLoading(true);
    
    // 直接从 state 获取选中的菜单ID
    const finalMenuKeys = checkedKeys || [];

    
    const queryParams = {
      roleId: currentRecord?.roleId,
      ids: finalMenuKeys,
    };

    // console.log("提交参数:", queryParams);

    const response = await http.post(api.getSaveRoleMenu, queryParams);
    
    if (response.code === "1") {
      message.success("Add menu successfully.");
      setVisible(false);
      form.resetFields();
      setSearchValue("");
      setCheckedKeys([]); // 重置选中状态
      onSuccess?.();
    } else {
      message.error(response.msg);
    }
  } catch (error) {
    console.error("提交失败:", error);
    message.error("Failed to save menu");
  } finally {
    setLoading(false);
  }
};

  return (
    <Modal
      title={
        <Flex justify="flex-start" align="flex-start" className="model_title">
          <EditOutlined className="model_title_mr10" />
          <Typography.Title level={4} className="mg">
            ROLE MENU
          </Typography.Title>
        </Flex>
      }
      width="40%"
      open={visible}
      maskClosable={false} 
      onCancel={() => {
        setVisible(false);
        form.resetFields();
        setSearchValue("");
      }}
      footer={
        <div style={{ display: "flex", justifyContent: "center", gap: "16px" }}>
          <Button
            key="back"
            onClick={() => {
              setVisible(false);
              form.resetFields();
              setSearchValue("");
            }}
          >
            Cancel
          </Button>
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={() => form.submit()}
          >
            Submit
          </Button>
        </div>
      }
      destroyOnClose
    >
      <Spin spinning={loading} tip="Loading...">
        <div style={{ height: "100%" }}>
          <div className="model_top" style={{ width: "100%" }}>
            <Flex justify="flex-start" align="flex-start">
              <Form
                form={form}
                onFinish={onFinishMenu}
                style={{ marginLeft: "13%", width: "75%", marginTop: "10px" }}
              >
                <Form.Item
                  name="menu"
                  label="Menu"
                >
                  <div>
                    <Input
                      placeholder="Search menu..."
                      prefix={<SearchOutlined />}
                      value={searchValue}
                      onChange={handleSearch}
                      allowClear
                      style={{ marginBottom: "12px" }}
                    />
                    <Tree
                      treeData={filteredTreeData}
                      expandedKeys={expandedKeysState}
                      onExpand={handleExpand}
                      checkable
                      checkStrictly={true}
                      selectable={false}
                      style={{
                        width: "100%",
                        height: 800,
                        borderRadius: "12px",
                        padding: "12px",
                        overflow: "auto",
                      }}
                      checkedKeys={validCheckedKeys}
                      onCheck={handleCheck}
                    />
                  </div>
                </Form.Item>
                <Form.Item name="_record" noStyle>
                  <Input type="hidden" />
                </Form.Item>
              </Form>
            </Flex>
          </div>
        </div>
      </Spin>
    </Modal>
  );
});

export default RoleMenuModal;